import { Alert, Col, Layout, Row } from 'antd';
import Title from 'antd/lib/typography/Title';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { StoredTeam } from '../../../hooks/types';
import useDeleteTeam from '../../../hooks/useDeleteTeam';
import { useLoggedInUser } from '../../../hooks/useLoggedInUser';
import { useNotify } from '../../../hooks/useNotify';
import { usePermission } from '../../../hooks/usePermission';
import useQueryTeamById from '../../../hooks/useQueryTeamById';
import { onApolloError } from '../../../lib/graphql/helpers';
import { errorMessage, successMessage } from '../../../lib/utils/messages';
import routes from '../../App/routes';
import BackButton from '../../BackButton';
import PageLoadingSpinner from '../../PageLoadingSpinner';
import { Optional } from '../../Shared';
import { TeamType } from '../types';
import { ArchiveTeamButton } from './ArchiveTeamButton';
import TeamForm from './TeamForm';

const defaultTeam: StoredTeam = {
  id: '',
  name: '',
  team_type: TeamType.LocalIMT.toString(),
  team_role_assignments: [],
  record_status: 'Active',
};

const uuidRegex =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/u;

/**
 * Renders a form for editing/creating a Team, Team Role Assignment and User Role Assignment.
 */
const TeamEditor = () => {
  const { notify } = useNotify();
  const { teamId } = useParams<{ readonly teamId: string | undefined }>();
  const { loggedInUser } = useLoggedInUser();
  const navigate = useNavigate();
  const permission = usePermission(teamId, loggedInUser);
  const [currentTeam, setCurrentTeam] = useState<StoredTeam | undefined>(
    defaultTeam
  );
  const isNewTeam = teamId === 'new' || teamId === undefined;

  // Get the details of the specific team that has been requested, specified by the 'id' in the route.
  // Save those details in local state for putting into the edit form.
  // Only perform this query if an id was provided.
  const { loading: loadingTeam, refetch } = useQueryTeamById(
    (team) => setCurrentTeam(team),
    onApolloError(notify.error),
    teamId,
    isNewTeam || !uuidRegex.test(teamId)
  );

  // Make sure reload the team data with latest, invalidate the cache.
  // This handles situation like role assignment updated on roster and requires an update in Team form.
  useEffect(() => {
    const refetchTeamData = async () => {
      await refetch();
    };

    if (!isNewTeam) {
      void refetchTeamData();
    }
  }, [refetch, isNewTeam]);

  const [deleteTeam] = useDeleteTeam(onApolloError(notify.error));

  const teamNotFound =
    (currentTeam?.id === undefined && !isNewTeam) || currentTeam === undefined;

  if (loggedInUser === undefined || loadingTeam) {
    return <PageLoadingSpinner />;
  }

  if (teamNotFound) {
    return (
      <Alert type="error" message={errorMessage.teamInformationNotFound} />
    );
  }
  const onArchive = (reason: string) => {
    void deleteTeam({
      variables: { teamId, reason },
      onCompleted: () => {
        notify.success(successMessage.teamArchived);
        navigate(routes.allTeams, { replace: true });
      },
    });
  };

  return (
    <Layout>
      <Row
        gutter={20}
        style={{
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          margin: '0 auto 60px auto',
        }}
      >
        <Col span={4}>
          <BackButton />
        </Col>

        <Col span={16} style={{ textAlign: 'center' }}>
          <Title level={2}>
            {isNewTeam
              ? 'New Team'
              : permission.teamManageRosterAndTeam
                ? 'Configure Team'
                : 'View Team'}
          </Title>
        </Col>

        <Col span={4}>
          <Optional
            show={
              !isNewTeam &&
              permission.teamDelete &&
              currentTeam.team_type !== 'SOC'
            }
          >
            <ArchiveTeamButton onArchive={onArchive} />
          </Optional>
        </Col>
      </Row>

      <Row style={{ justifyContent: 'center', alignItems: 'center' }}>
        <Col
          flex="auto"
          span={20}
          style={{ justifyContent: 'center', alignItems: 'center' }}
        >
          <TeamForm isNewTeam={isNewTeam} currentTeam={currentTeam} />
        </Col>
      </Row>
    </Layout>
  );
};

export default TeamEditor;
